import { getFirestore, collection, addDoc, query, where, getDocs, orderBy, Timestamp, limit } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const db = getFirestore();

// Generate a unique session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('aw_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('aw_session_id', sessionId);
    sessionStorage.setItem('aw_session_start', Date.now().toString());
  }
  return sessionId;
};

// Get device info
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
  
  let browser = 'Unknown';
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
  
  let os = 'Unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  
  return {
    deviceType: isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop',
    browser,
    os,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    language: navigator.language,
  };
};

// Get referrer info
const getReferrer = () => {
  const referrer = document.referrer;
  if (!referrer) return 'Direct';
  try {
    const url = new URL(referrer);
    return url.hostname;
  } catch {
    return referrer;
  }
};

// Categorize traffic source from referrer
export const categorizeSource = (referrer: string): string => {
  if (!referrer || referrer === 'Direct') return 'Direct';
  const r = referrer.toLowerCase();
  if (r.includes('google') || r.includes('bing') || r.includes('yahoo') || r.includes('duckduckgo')) return 'Google Search';
  if (r.includes('instagram') || r.includes('ig.')) return 'Instagram';
  if (r.includes('facebook') || r.includes('fb.')) return 'Facebook';
  if (r.includes('whatsapp') || r.includes('wa.me')) return 'WhatsApp';
  if (r.includes('twitter') || r.includes('x.com') || r.includes('t.co')) return 'Twitter/X';
  if (r.includes('linkedin')) return 'LinkedIn';
  if (r.includes('youtube')) return 'YouTube';
  return 'Referral';
};

// Get UTM parameters
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') || null,
    utmMedium: params.get('utm_medium') || null,
    utmCampaign: params.get('utm_campaign') || null,
    utmTerm: params.get('utm_term') || null,
    utmContent: params.get('utm_content') || null,
  };
};

// Track page view
export const trackPageView = async (path: string) => {
  try {
    const auth = getAuth();
    const sessionId = getSessionId();
    const deviceInfo = getDeviceInfo();
    const referrer = getReferrer();
    
    const utmParams = getUtmParams();
    const source = categorizeSource(referrer);
    
    await addDoc(collection(db, 'analytics_pageviews'), {
      path,
      sessionId,
      userId: auth.currentUser?.uid || null,
      referrer,
      source,
      ...deviceInfo,
      ...utmParams,
      timestamp: Timestamp.now(),
      date: new Date().toISOString().split('T')[0],
      hour: new Date().getHours(),
    });
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

// Track custom event (button clicks, form submissions, etc.)
export const trackEvent = async (eventName: string, eventData?: Record<string, any>) => {
  try {
    const auth = getAuth();
    const sessionId = getSessionId();
    
    await addDoc(collection(db, 'analytics_events'), {
      eventName,
      eventData: eventData || {},
      sessionId,
      userId: auth.currentUser?.uid || null,
      path: window.location.pathname,
      timestamp: Timestamp.now(),
      date: new Date().toISOString().split('T')[0],
    });
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

// Track session end (called on page unload)
export const trackSessionEnd = async () => {
  try {
    const sessionId = sessionStorage.getItem('aw_session_id');
    const sessionStart = sessionStorage.getItem('aw_session_start');
    if (!sessionId || !sessionStart) return;
    
    const duration = Math.floor((Date.now() - parseInt(sessionStart)) / 1000);
    const auth = getAuth();
    
    // Use sendBeacon for reliability on page unload
    const data = {
      sessionId,
      userId: auth.currentUser?.uid || null,
      duration,
      endedAt: new Date().toISOString(),
    };
    
    // Store session data
    await addDoc(collection(db, 'analytics_sessions'), {
      ...data,
      timestamp: Timestamp.now(),
      date: new Date().toISOString().split('T')[0],
    });
  } catch (error) {
    console.error('Failed to track session end:', error);
  }
};

// Initialize analytics tracking
export const initAnalytics = () => {
  // Track initial page view
  trackPageView(window.location.pathname);
  
  // Track page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackSessionEnd();
    }
  });
  
  // Track before unload
  window.addEventListener('beforeunload', () => {
    trackSessionEnd();
  });
};

// Fetch analytics data for admin dashboard
export interface AnalyticsData {
  pageviews: {
    date: string;
    count: number;
    path: string;
  }[];
  events: {
    date: string;
    eventName: string;
    count: number;
  }[];
  sessions: {
    date: string;
    count: number;
    avgDuration: number;
  }[];
  deviceBreakdown: {
    deviceType: string;
    count: number;
  }[];
  browserBreakdown: {
    browser: string;
    count: number;
  }[];
  topPages: {
    path: string;
    count: number;
  }[];
  topReferrers: {
    referrer: string;
    count: number;
  }[];
  dailyVisitors: {
    date: string;
    uniqueVisitors: number;
    pageviews: number;
  }[];
}

export const fetchAnalyticsData = async (startDate: Date, endDate: Date): Promise<AnalyticsData> => {
  const startDateStr = startDate.toISOString().split('T')[0];
  const endDateStr = endDate.toISOString().split('T')[0];
  
  // Fetch pageviews
  const pageviewsQuery = query(
    collection(db, 'analytics_pageviews'),
    where('date', '>=', startDateStr),
    where('date', '<=', endDateStr),
    orderBy('date', 'desc'),
    limit(5000)
  );
  
  const pageviewsSnapshot = await getDocs(pageviewsQuery);
  const pageviews = pageviewsSnapshot.docs.map(doc => doc.data());
  
  // Fetch events
  const eventsQuery = query(
    collection(db, 'analytics_events'),
    where('date', '>=', startDateStr),
    where('date', '<=', endDateStr),
    orderBy('date', 'desc'),
    limit(2000)
  );
  
  const eventsSnapshot = await getDocs(eventsQuery);
  const events = eventsSnapshot.docs.map(doc => doc.data());
  
  // Fetch sessions
  const sessionsQuery = query(
    collection(db, 'analytics_sessions'),
    where('date', '>=', startDateStr),
    where('date', '<=', endDateStr),
    orderBy('date', 'desc'),
    limit(2000)
  );
  
  const sessionsSnapshot = await getDocs(sessionsQuery);
  const sessions = sessionsSnapshot.docs.map(doc => doc.data());
  
  // Process data
  const dailyStats: Record<string, { visitors: Set<string>; pageviews: number }> = {};
  const pathCounts: Record<string, number> = {};
  const referrerCounts: Record<string, number> = {};
  const deviceCounts: Record<string, number> = {};
  const browserCounts: Record<string, number> = {};
  const eventCounts: Record<string, number> = {};
  
  pageviews.forEach((pv: any) => {
    // Daily stats
    if (!dailyStats[pv.date]) {
      dailyStats[pv.date] = { visitors: new Set(), pageviews: 0 };
    }
    dailyStats[pv.date].visitors.add(pv.sessionId);
    dailyStats[pv.date].pageviews++;
    
    // Path counts
    pathCounts[pv.path] = (pathCounts[pv.path] || 0) + 1;
    
    // Referrer counts
    referrerCounts[pv.referrer] = (referrerCounts[pv.referrer] || 0) + 1;
    
    // Device counts
    deviceCounts[pv.deviceType] = (deviceCounts[pv.deviceType] || 0) + 1;
    
    // Browser counts
    browserCounts[pv.browser] = (browserCounts[pv.browser] || 0) + 1;
  });
  
  events.forEach((ev: any) => {
    eventCounts[ev.eventName] = (eventCounts[ev.eventName] || 0) + 1;
  });
  
  // Calculate session stats
  const sessionsByDate: Record<string, { count: number; totalDuration: number }> = {};
  sessions.forEach((s: any) => {
    if (!sessionsByDate[s.date]) {
      sessionsByDate[s.date] = { count: 0, totalDuration: 0 };
    }
    sessionsByDate[s.date].count++;
    sessionsByDate[s.date].totalDuration += s.duration || 0;
  });
  
  return {
    pageviews: Object.entries(pathCounts).map(([path, count]) => ({
      date: '',
      path,
      count,
    })),
    events: Object.entries(eventCounts).map(([eventName, count]) => ({
      date: '',
      eventName,
      count,
    })),
    sessions: Object.entries(sessionsByDate).map(([date, data]) => ({
      date,
      count: data.count,
      avgDuration: data.count > 0 ? Math.round(data.totalDuration / data.count) : 0,
    })),
    deviceBreakdown: Object.entries(deviceCounts).map(([deviceType, count]) => ({
      deviceType,
      count,
    })),
    browserBreakdown: Object.entries(browserCounts).map(([browser, count]) => ({
      browser,
      count,
    })),
    topPages: Object.entries(pathCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([path, count]) => ({ path, count })),
    topReferrers: Object.entries(referrerCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([referrer, count]) => ({ referrer, count })),
    dailyVisitors: Object.entries(dailyStats)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, data]) => ({
        date,
        uniqueVisitors: data.visitors.size,
        pageviews: data.pageviews,
      })),
  };
};
